// Runs the CS patient validators for a patient and returns a serializable
// summary the ticket agent can consume as LLM context.

import { runValidators, supportValidators } from "./validators";
import {
  getPatientData,
  getDoctorByUserId,
  getTranscribeJobsByPatient,
  getSummarizeJobsByPatient,
  getTraceByPatient,
} from "./patientApi";

/**
 * Fetch the same support data the CS patient page uses and run all
 * validators against it.
 *
 * Returns { patient_id, results: [{ id, name, status, messages }] } or null
 * if the patient could not be loaded.
 */
export async function collectValidatorResults(patientId) {
  const patient = await getPatientData(patientId);
  if (!patient || patient.error || Object.keys(patient).length === 0) {
    return null;
  }

  const userId = patient.user_id;
  const [doctorResult, transcribeResult, summarizeResult, traceResult] =
    await Promise.all([
      userId
        ? getDoctorByUserId(userId)
        : Promise.resolve({ error: "No doctor user ID" }),
      getTranscribeJobsByPatient(patientId),
      getSummarizeJobsByPatient(patientId),
      getTraceByPatient(patientId),
    ]);

  const pick = (result) => (result?.error ? null : result);
  const context = {
    patient,
    doctor: pick(doctorResult),
    transcription: pick(transcribeResult),
    summarization: pick(summarizeResult),
    trace: pick(traceResult),
    fetchErrors: {
      doctor: doctorResult?.error || null,
      transcription: transcribeResult?.error || null,
      summarization: summarizeResult?.error || null,
      trace: traceResult?.error || null,
    },
    isLoading: false,
  };

  const results = await runValidators(supportValidators, context);

  return {
    patient_id: String(patientId),
    results: results.map((r) => ({
      id: r.test.id,
      name: r.test.name,
      status: r.status,
      messages: r.messages,
    })),
  };
}
